/**
 * 参考音频上传组件
 *
 * - 仅支持本地上传（不支持手动输入 URL）。
 * - 选完文件后会先在浏览器端读取音频时长，
 *   若超出 [minDurationSec, maxDurationSec] 区间会直接报错并放弃上传。
 * - 时长边界由调用方按 provider 能力声明传入；未传则不做时长校验。
 */

import { useRef, useState } from 'react';
import { Upload, X, Loader2, Music } from 'lucide-react';
import { platformFetch, readEnvelope } from '../../../platform/http';
import { readMediaDuration, formatSeconds } from '../utils/readMediaDuration';

interface VideoAudioReferenceUploaderProps {
  value: string[];
  onChange: (value: string[]) => void;
  maxCount?: number;
  /** 允许的最小时长（秒）；未传则不校验下限 */
  minDurationSec?: number;
  /** 允许的最大时长（秒）；未传则不校验上限 */
  maxDurationSec?: number;
}

const MAX_SIZE_MB = 15;
const ACCEPT = '.mp3,.wav,.m4a,.ogg,.aac,.flac';

export function VideoAudioReferenceUploader({
  value,
  onChange,
  maxCount = 1,
  minDurationSec,
  maxDurationSec,
}: VideoAudioReferenceUploaderProps) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const canAdd = value.length < maxCount;

  const hasMin = typeof minDurationSec === 'number' && minDurationSec > 0;
  const hasMax = typeof maxDurationSec === 'number' && maxDurationSec > 0;

  const durationHint = (() => {
    if (hasMin && hasMax) {
      return `时长需在 ${formatSeconds(minDurationSec!)}~${formatSeconds(maxDurationSec!)} 秒之间`;
    }
    if (hasMin) return `时长需 ≥ ${formatSeconds(minDurationSec!)} 秒`;
    if (hasMax) return `时长需 ≤ ${formatSeconds(maxDurationSec!)} 秒`;
    return '';
  })();

  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];

    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(`文件大小超过 ${MAX_SIZE_MB}MB 限制`);
      return;
    }
    if (!canAdd) return;

    setError('');

    // 1) 时长校验（可选）
    if (hasMin || hasMax) {
      try {
        const duration = await readMediaDuration(file, 'audio');
        if (hasMin && duration < (minDurationSec as number)) {
          setError(
            `音频时长 ${formatSeconds(duration)} 秒过短，需 ≥ ${formatSeconds(minDurationSec!)} 秒`
          );
          return;
        }
        if (hasMax && duration > (maxDurationSec as number)) {
          setError(
            `音频时长 ${formatSeconds(duration)} 秒过长，需 ≤ ${formatSeconds(maxDurationSec!)} 秒`
          );
          return;
        }
      } catch (err: any) {
        setError(err?.message || '读取音频时长失败');
        return;
      }
    }

    // 2) 通过校验后才上传
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      // platformFetch 检测到 FormData 自动不设 Content-Type；401 统一清会话并广播
      const data = await readEnvelope<{ storageKey: string }>(
        await platformFetch('/videos/upload-audio', {
          method: 'POST',
          body: formData,
        }),
        '上传失败'
      );

      onChange([...value, data.storageKey]);
    } catch (err: any) {
      setError(err?.message || '上传失败');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = (idx: number) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="block text-sm font-medium text-light-text">
          参考音频{maxCount > 1 ? `（最多 ${maxCount} 个）` : '（可选）'}
        </label>
      </div>

      {/* 已添加的音频列表 */}
      {value.length > 0 && (
        <div className="space-y-1.5 mb-2">
          {value.map((_item, idx) => (
            <div
              key={idx}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-light-border bg-light-hover"
            >
              <Music className="w-4 h-4 text-accent-blue flex-shrink-0" />
              <span className="text-xs text-light-text truncate flex-1">
                已上传 #{idx + 1}
              </span>
              <button
                type="button"
                onClick={() => handleRemove(idx)}
                className="p-0.5 rounded text-rose-500 hover:bg-rose-50"
                aria-label={`移除参考音频 #${idx + 1}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* 上传按钮（只保留文件上传入口） */}
      {canAdd && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-light-border text-sm text-light-muted hover:border-accent-blue hover:text-accent-blue transition-all disabled:opacity-50"
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            {uploading ? '上传中' : '上传文件'}
          </button>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        onChange={(e) => handleFileSelect(e.target.files)}
        className="hidden"
      />

      {error && <p className="mt-1 text-xs text-rose-500">{error}</p>}
      <p className="mt-1 text-xs text-light-muted">
        支持 mp3/wav/m4a/ogg/aac/flac，≤{MAX_SIZE_MB}MB
        {durationHint ? `，${durationHint}` : ''}
      </p>
    </div>
  );
}
